import { DateTime } from 'luxon';
import {
  isString,
  unionGuard,
  typeGuardGenerator,
  typeGuardTestGenerator,
  isNumber,
  isUndefinedOrNull,
  isArrayOfGenerator,
} from '@metools/tcheck';

import {
  isValidDateTimeString,
  isValidDateTimeStringOrUndefinedOrNull,
  isStringOrUndefinedOrNull,
  isBooleanOrUndefinedOrNull,
  isNumberOrUndefinedOrNull,
  isStringArray,
} from '@qsrsoft/myq-core';

import { birthdayCalendar } from '@/views/dashboard/widgets/calendar/constants/constants';

/**
 * @typedef {import('@metools/tcheck').TypeGuard<T>} TypeGuard
 * @template T
 */

// EventAttachmentJSON definition
/**
 * @typedef {Object} EventAttachmentJSON
 * @property {string} id
 * @property {string} key
 * @property {string} name
 * @property {string} mimeType
 * @property {string} createdTime
 * @property {string} createdByUserId
 */

const isEventAttachmentJSONShape = {
  id: isString,
  key: isString,
  name: isString,
  mimeType: isString,
  createdTime: isValidDateTimeString,
  createdByUserId: isString,
};

/** @type {TypeGuard<EventAttachmentJSON>} */
export const isEventAttachmentJSON = typeGuardGenerator(
  isEventAttachmentJSONShape,
);

/** @type {TypeGuard<EventAttachmentJSON[]>} */
export const isEventAttachmentJSONArray = isArrayOfGenerator(
  isEventAttachmentJSON,
);

const isEventAttachmentJSONArrayOrUndefinedOrNull = unionGuard(
  isEventAttachmentJSONArray,
  isUndefinedOrNull,
);

// MyQEventJSON definition
/**
 * @typedef {Object} MyQEventJSON
 * @property {string} id
 * @property {string} orgId
 * @property {string} title
 * @property {string} start
 * @property {string} end
 * @property {string} createdTime
 * @property {string} createdByUserId
 * @property {string} lastEditedByUserId
 * @property {string} lastEditedTime
 * // Optional fields
 * @property {string} [calendarId]
 * @property {string} [description]
 * @property {string} [locationId]
 * @property {string} [locationName]
 * @property {string} [letterCommentClass]
 * @property {boolean} [allDay]
 * @property {string} [recurring]
 * @property {string} [recurringEnd]
 * @property {string} [certificationId]
 * @property {string} [originalEventId]
 * @property {boolean} [isCommsHubEvent]
 * @property {string} [sourceCalendarId]
 * @property {string[]} [roleAccessList]
 * @property {string[]} [locationAccessList]
 * @property {boolean} [isClassEvent]
 * @property {EventAttachmentJSON[]} [attachments]
 * @property {boolean} [isFeedPost]
 * @property {number} [feedPostLeadDays]
 * @property {boolean} [isPinned]
 */

const isStringArrayOrUndefinedOrNull = unionGuard(
  isStringArray,
  isUndefinedOrNull,
);

// RemoteSubscriptionEventJSON definition
/**
 * @typedef {Object} RemoteSubscriptionEventProps
 * @property {string} subscriptionCalendarId
 * @property {number} [sequence]
 * @property {string} [recurrenceId]
 */

/** @typedef {MyQEventJSON & RemoteSubscriptionEventProps} RemoteSubscriptionEventJSON */

const isMyQEventCommon = {
  id: isString,
  orgId: isString,
  title: isString,
  start: isValidDateTimeString,
  end: isValidDateTimeString,
  createdTime: isValidDateTimeString,
  createdByUserId: isString,
  lastEditedByUserId: isString,
  lastEditedTime: isValidDateTimeString,
  // Optional fields
  calendarId: isStringOrUndefinedOrNull,
  description: isStringOrUndefinedOrNull,
  locationId: isStringOrUndefinedOrNull,
  locationName: isStringOrUndefinedOrNull,
  letterCommentClass: isStringOrUndefinedOrNull,
  allDay: isBooleanOrUndefinedOrNull,
  recurring: isStringOrUndefinedOrNull,
  recurringEnd: isValidDateTimeStringOrUndefinedOrNull,
  certificationId: isStringOrUndefinedOrNull,
  originalEventId: isStringOrUndefinedOrNull,
  isClassEvent: isBooleanOrUndefinedOrNull,
  isCommsHubEvent: isBooleanOrUndefinedOrNull,
  roleAccessList: isStringArrayOrUndefinedOrNull,
  locationAccessList: isStringArrayOrUndefinedOrNull,
  attachments: isEventAttachmentJSONArrayOrUndefinedOrNull,
  isFeedPost: isBooleanOrUndefinedOrNull,
  feedPostLeadDays: isNumberOrUndefinedOrNull,
  isPinned: isBooleanOrUndefinedOrNull,
};

/** @type {TypeGuard<MyQEventJSON>} */
export const isMyQEventJSON = typeGuardGenerator(isMyQEventCommon);

/** @type {TypeGuard<RemoteSubscriptionEventJSON>} */
export const isRemoteSubscriptionEventJSON = typeGuardGenerator({
  ...isMyQEventCommon,
  subscriptionCalendarId: isString,
  sequence: isNumberOrUndefinedOrNull,
  recurrenceId: isStringOrUndefinedOrNull,
});

export class MyQEvent {
  /** @type {string} */
  _id;
  /** @type {string} */
  _title;
  /** @type {string} */
  _orgId;
  /** @type {string} */
  _start;
  /** @type {string} */
  _end;
  /** @type {string} */
  _createdTime;
  /** @type {string} */
  _createdByUserId;
  /** @type {string} */
  _lastEditedByUserId;
  /** @type {string} */
  _lastEditedTime;

  // Optional values

  /** @type {string | undefined} */
  _calendarId;
  /** @type {string | undefined} */
  _description;
  /** @type {string | undefined} */
  _locationId;
  /** @type {string | undefined} */
  _locationName;
  /** @type {string | undefined} */
  _letterCommentClass;
  /** @type {boolean | undefined} */
  _allDay;
  /** @type {string | undefined} */
  _recurring;
  /** @type {string | undefined} */
  _recurringEnd;
  /** @type {string | undefined} */
  _certificationId;
  /** @type {string | undefined} */
  _originalEventId;
  /** @type {boolean | undefined} */
  _isClassEvent;
  /** @type {boolean | undefined} */
  _isCommsHubEvent;
  /** @type {string[] | undefined} */
  _roleAccessList;
  /** @type {string[] | undefined} */
  _locationAccessList;
  /** @type {EventAttachmentJSON[] | undefined} */
  _attachments;
  /** @type {boolean | undefined} */
  _isFeedPost;
  /** @type {number | undefined} */
  _feedPostLeadDays;
  /** @type {boolean | undefined} */
  _isPinned;
  /**
   * @param {MyQEventJSON} eventJSON
   */
  constructor(eventJSON) {
    // this.eventJSON = eventJSON;

    this._id = eventJSON.id;
    this._orgId = eventJSON.orgId;
    this._title = eventJSON.title;
    this._start = eventJSON.start;
    this._end = eventJSON.end;
    this._createdTime = eventJSON.createdTime;
    this._createdByUserId = eventJSON.createdByUserId;
    this._lastEditedByUserId = eventJSON.lastEditedByUserId;
    this._lastEditedTime = eventJSON.lastEditedTime;

    // Optional values
    this._calendarId = eventJSON.calendarId;
    this._description = eventJSON.description;
    this._locationId = eventJSON.locationId;
    this._locationName = eventJSON.locationName;
    this._letterCommentClass = eventJSON.letterCommentClass;
    this._allDay = eventJSON.allDay ?? false;
    this._recurring = eventJSON.recurring;
    this._isClassEvent = eventJSON.isClassEvent ?? false;
    this._recurringEnd = eventJSON.recurringEnd;
    this._certificationId = eventJSON.certificationId;
    this._originalEventId = eventJSON.originalEventId;
    this._isCommsHubEvent = eventJSON.isCommsHubEvent ?? false;
    this._roleAccessList = eventJSON.roleAccessList ?? undefined;
    this._locationAccessList = eventJSON.locationAccessList ?? undefined;
    this._attachments = eventJSON.attachments ?? undefined;
    this._isFeedPost = eventJSON.isFeedPost ?? false;
    this._feedPostLeadDays = eventJSON.feedPostLeadDays ?? undefined;
    this._isPinned = eventJSON.isPinned ?? false;
  }

  get id() {
    return this._id;
  }
  get title() {
    return this._title;
  }
  get orgId() {
    return this._orgId;
  }
  get start() {
    if (this.allDay) {
      const dt = DateTime.fromISO(this._start);

      if (dt.isValid) {
        const val = dt.toUTC().toISODate();
        return val;
      }
    }

    return this._start;
  }
  /**
   * Returns the end time for the event. If the event is an all-day
   * event, it's just the date. The end date is inclusive.
   */
  get end() {
    if (this.allDay) {
      const dt = DateTime.fromISO(this._end);

      if (dt.isValid) {
        const val = dt.toUTC().toISODate();
        return val;
      }
    }
    return this._end;
  }
  get allDay() {
    return this._allDay ?? false;
  }
  get calendarId() {
    return this._calendarId;
  }
  get description() {
    return this._description;
  }
  get locationName() {
    return this._locationName;
  }
  get locationId() {
    return this._locationId;
  }
  get recurring() {
    return this._recurring;
  }
  get recurringEnd() {
    return this._recurringEnd;
  }
  get lastEditedByUserId() {
    return this._lastEditedByUserId;
  }
  get lastEditedTime() {
    return this._lastEditedTime;
  }
  get letterCommentClass() {
    return this._letterCommentClass;
  }
  get certificationId() {
    return this._certificationId;
  }
  get isClassEvent() {
    return this._isClassEvent;
  }
  get originalEventId() {
    return this._originalEventId;
  }
  get isCommsHubEvent() {
    return this._isCommsHubEvent;
  }
  get roleAccessList() {
    return this._roleAccessList;
  }
  get locationAccessList() {
    return this._locationAccessList;
  }
  get attachments() {
    return this._attachments;
  }
  get isFeedPost() {
    return this._isFeedPost;
  }
  get feedPostLeadDays() {
    return this._feedPostLeadDays;
  }
  get isPinned() {
    return this._isPinned;
  }
  /**
   * @returns {MyQEventJSON}
   */
  toJSON() {
    return {
      id: this._id,
      orgId: this._orgId,
      title: this._title,
      start: this._start,
      end: this._end,
      createdTime: this._createdTime,
      createdByUserId: this._createdByUserId,
      lastEditedByUserId: this._lastEditedByUserId,
      lastEditedTime: this._lastEditedTime,
      calendarId: this._calendarId,
      description: this._description,
      locationId: this._locationId,
      isClassEvent: this._isClassEvent,
      locationName: this._locationName,
      letterCommentClass: this._letterCommentClass,
      allDay: this._allDay,
      recurring: this._recurring,
      recurringEnd: this._recurringEnd,
      certificationId: this._certificationId,
      originalEventId: this._originalEventId,
      isCommsHubEvent: this._isCommsHubEvent,
      roleAccessList: this._roleAccessList,
      locationAccessList: this._locationAccessList,
      attachments: this._attachments,
      isFeedPost: this._isFeedPost,
      feedPostLeadDays: this._feedPostLeadDays,
      isPinned: this._isPinned,
    };
  }

  static isMyQEventJSON = isMyQEventJSON;
}

export class RemoteSubscriptionEvent extends MyQEvent {
  /** @type {string} */
  _subscriptionCalendarId;
  /** @type {number | undefined} */
  _sequence;
  /** @type {string | undefined} */
  _recurrenceId;

  /**
   * @param {RemoteSubscriptionEventJSON} eventJSON
   */
  constructor(eventJSON) {
    super(eventJSON);

    this._subscriptionCalendarId = eventJSON.subscriptionCalendarId;
    this._sequence = eventJSON.sequence;
    this._recurrenceId = eventJSON.recurrenceId;
  }

  get subscriptionCalendarId() {
    return this._subscriptionCalendarId;
  }
  get sequence() {
    return this._sequence;
  }
  get recurrenceId() {
    return this._recurrenceId;
  }

  /**
   * @returns {RemoteSubscriptionEventJSON}
   */
  toJSON() {
    return {
      ...super.toJSON(),
      subscriptionCalendarId: this._subscriptionCalendarId,
      sequence: this._sequence,
      recurrenceId: this._recurrenceId,
    };
  }

  static isRemoteSubscriptionEventJSON = isRemoteSubscriptionEventJSON;
}

// BirthdayAnniversaryAPIResponse
/**
 * @typedef {object} BirthdayAnniversaryAPIResponse
 * @property {string} firstName
 * @property {string} lastName
 * @property {number | string | undefined | null} geid
 * @property {string} birthday
 * @property {string} orgStartDate
 * @property {string[]} [locationAccessList]
 * @property {number | string | undefined | null} storeNum
 */

const isNumberOrStringOrUndefinedOrNull = unionGuard(
  isNumber,
  isString,
  isUndefinedOrNull,
);

const isBirthdayAnniversaryResponseCommon = {
  firstName: isString,
  lastName: isString,
  geid: isNumberOrStringOrUndefinedOrNull,
  birthday: isString,
  orgStartDate: isString,
  storeNum: isNumberOrStringOrUndefinedOrNull,
};

/** @type {TypeGuard<BirthdayAnniversaryAPIResponse>} */
export const isBirthdayAnniversaryAPIResponse = typeGuardGenerator(
  isBirthdayAnniversaryResponseCommon,
);
const isBirthdayAnniversaryAPIResponseTest = typeGuardTestGenerator(
  isBirthdayAnniversaryResponseCommon,
);

/**
 * Calculate a birthday between two dates. If birthday is not
 * in the range, return undefined
 * @param {string} dateStr
 * @param {string} date1
 * @param {string} date2
 * @returns {DateTime<true> | undefined}
 */
function getDateBetweenDates(dateStr, date1, date2) {
  const start = DateTime.fromISO(date1).startOf('day');
  const end = DateTime.fromISO(date2).endOf('day');

  const date = DateTime.fromISO(dateStr);
  if (!date.isValid) {
    return undefined;
  }

  let thisYearBirthday = date.set({ year: start.year });
  if (thisYearBirthday < start) {
    thisYearBirthday = thisYearBirthday.plus({ years: 1 });
  }

  if (thisYearBirthday >= start && thisYearBirthday <= end) {
    return thisYearBirthday;
  }

  return undefined;
}

/**
 * Prepend the tenure year count to an anniversary title.
 * @param {string} baseName - e.g. "John Smith Work Anniversary (123)"
 * @param {string | undefined} orgStartDate - ISO date of hire
 * @param {string} eventDate - ISO date of this occurrence
 * @returns {string}
 */
function buildAnniversaryTitle(baseName, orgStartDate, eventDate) {
  if (!orgStartDate) {
    return baseName;
  }

  const startYear = DateTime.fromISO(orgStartDate).year;
  const eventYear = DateTime.fromISO(eventDate).year;
  const years = eventYear - startYear;

  if (years > 0) {
    return baseName.replace(
      'Work Anniversary',
      `${years} Year Work Anniversary`,
    );
  }

  return baseName;
}

// BirthdayAnniversaryEventInput
/**
 * @typedef {object} BirthdayAnniversaryEventInput
 * @property {string} orgId
 * @property {string} title
 * @property {string} start
 * @property {string} end
 * @property {string} [description]
 * @property {string} [orgStartDate]
 * @property {string} [baseName]
 * @property {string[]} [locationAccessList]
 */

export class BirthdayEvent extends MyQEvent {
  /** @param {BirthdayAnniversaryEventInput} eventJSON */
  constructor(eventJSON) {
    super({
      id: JSON.stringify(eventJSON),
      orgId: eventJSON.orgId,
      title: eventJSON.title,
      start: eventJSON.start,
      end: eventJSON.end,
      allDay: true,
      calendarId: birthdayCalendar,
      locationAccessList: eventJSON.locationAccessList,
      createdTime: new Date().toISOString(),
      createdByUserId: 'system',
      lastEditedByUserId: 'system',
      lastEditedTime: new Date().toISOString(),
    });
  }

  /**
   * Calculate a birthday between two dates. If birthday is not
   * in the range, return undefined
   * @param {string} date1
   * @param {string} date2
   * @returns {BirthdayEvent | undefined}
   */
  getBirthdayBetweenDates(date1, date2) {
    const date = getDateBetweenDates(this.start, date1, date2);

    if (date) {
      return new BirthdayEvent({
        orgId: this.orgId,
        title: this.title,
        start: date.toISODate(),
        end: date.toISODate(),
        locationAccessList: this.locationAccessList,
      });
    }

    return undefined;
  }

  /**
   * @param {unknown} input
   * @param {string} orgId
   * @returns {BirthdayEvent}
   */
  static fromJSON(input, orgId) {
    if (!isBirthdayAnniversaryAPIResponse(input)) {
      const missing = isBirthdayAnniversaryAPIResponseTest(input);
      throw new Error(`Invalid birthday API response: ${missing.join(', ')}`);
    }

    const name = `${input.firstName} ${input.lastName}`;

    // Of format February-16
    const date =
      input.birthday === 'February-29'
        ? DateTime.fromObject({ month: 2, day: 28 })
        : DateTime.fromFormat(input.birthday, 'LLLL-dd');

    const dt = DateTime.now()
      .set({ month: date.month, day: date.day })
      .startOf('day');

    const locationAccessList =
      input.storeNum != null ? [String(input.storeNum)] : undefined;

    const temp = new BirthdayEvent({
      orgId,
      title: `${name} Birthday${isNumber(input.storeNum) ? ` (${input.storeNum})` : ''}`,
      start: dt.toISODate(),
      end: dt.toISODate(),
      locationAccessList,
    });

    return temp;
  }
}

export class AnniversaryEvent extends MyQEvent {
  /** @param {BirthdayAnniversaryEventInput} eventJSON */
  constructor(eventJSON) {
    super({
      id: JSON.stringify(eventJSON),
      orgId: eventJSON.orgId,
      title: eventJSON.title,
      description: eventJSON.description,
      start: eventJSON.start,
      end: eventJSON.end,
      allDay: true,
      calendarId: 'anniversaries',
      createdTime: new Date().toISOString(),
      createdByUserId: 'system',
      lastEditedByUserId: 'system',
      lastEditedTime: new Date().toISOString(),
      locationAccessList: eventJSON.locationAccessList,
    });

    /** @type {string | undefined} Original hire date, used to calculate tenure years in the title */
    this.orgStartDate = eventJSON.orgStartDate;

    /** @type {string | undefined} Title without the year count, e.g. "John Smith Work Anniversary (123)" */
    this.baseName = eventJSON.baseName;
  }

  /**
   * Calculate a birthday between two dates. If birthday is not
   * in the range, return undefined
   * @param {string} date1
   * @param {string} date2
   * @returns {AnniversaryEvent | undefined}
   */
  getAnniversaryBetweenDates(date1, date2) {
    const date = getDateBetweenDates(this.start, date1, date2);

    if (date) {
      const temp = new AnniversaryEvent({
        orgId: this.orgId,
        title: buildAnniversaryTitle(
          this.baseName,
          this.orgStartDate,
          date.toISODate(),
        ),
        description: this.description,
        start: date.toISODate(),
        end: date.toISODate(),
        orgStartDate: this.orgStartDate,
        baseName: this.baseName,
        locationAccessList: this.locationAccessList,
      });
      return temp;
    }

    return undefined;
  }

  /**
   * @param {unknown} input
   * @param {string} orgId
   * @returns {AnniversaryEvent}
   */
  static fromJSON(input, orgId) {
    if (!isBirthdayAnniversaryAPIResponse(input)) {
      const missing = isBirthdayAnniversaryAPIResponseTest(input);
      throw new Error(
        `Invalid anniversary API response: ${missing.join(', ')}`,
      );
    }

    const name = `${input.firstName} ${input.lastName}`;

    const anniDate = DateTime.fromISO(input.orgStartDate);
    if (!anniDate.isValid) {
      throw new Error(`Invalid OrgStartDate format: ${input.orgStartDate}`);
    }

    const dt = DateTime.now()
      .set({ month: anniDate.month, day: anniDate.day })
      .startOf('day');

    const baseName = `${name} Work Anniversary${isNumber(input.storeNum) ? ` (${input.storeNum})` : ''}`;
    const eventDate = dt.toISODate();
    const years = dt.year - anniDate.year;

    const locationAccessList =
      input.storeNum != null ? [String(input.storeNum)] : undefined;

    const eventConfig = {
      orgId,
      title: buildAnniversaryTitle(baseName, input.orgStartDate, eventDate),
      start: eventDate,
      end: eventDate,
      orgStartDate: input.orgStartDate,
      baseName,
      locationAccessList,
    };

    if (years > 0) {
      eventConfig.description = `Congratulations to ${name} on ${years} ${years === 1 ? 'year' : 'years'}!`;
    }

    return new AnniversaryEvent(eventConfig);
  }
}
